import styles from './UserColumn.module.scss';
import { CopyToClipboard } from '@src/utils/componentCasts';
import { SchoolUser } from '@src/api/graphql-codegen';
import { User, UserRoleEnum, UserStatus } from '@src/types';
import { Tooltip as AntTooltip, } from 'antd';
import { IconDistAdmin, IconUser, IconUserManage, timeAgo } from '@src/constants';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import PasswordIcon from 'src/assets/icons/password.svg?react';
import CopyIcon from 'src/assets/icons/copy.svg?react';

type Props = {
  user: SchoolUser;
  isDistrictAdmin: boolean;
  handleCopyToClipboard: (text: string) => void;
  handleUserEdit: (user: User, action: 'EDIT' | 'PASSWORD_RESET') => void;
};

function text_truncate(str: string, length: number, ending?: string) {
  if (length == null) {
    length = 20;
  }
  if (ending == null) {
    ending = '...';
  }
  if (str.length > length) {
    return str.substring(0, length - ending.length) + ending;
  } else {
    return str;
  }
};

const getIconByRole = (role: string) => {
  switch (role) {
    case UserRoleEnum.DistrictAdmin:
      return IconDistAdmin;
    case UserRoleEnum.Admin:
      return IconUserManage;
    case UserRoleEnum.User:
      return IconUser;
    case UserRoleEnum.SuperAdmin:
      return IconUserManage;
    default: {
      return IconUser;
    }
  }
}

const getRoleTip = (role: string) => {
  switch (role) {
    case UserRoleEnum.DistrictAdmin:
      return 'OP District Administrator';
    case UserRoleEnum.Admin:
      return 'OP Administrator';
    case UserRoleEnum.User:
      return 'Educator';
    case UserRoleEnum.SuperAdmin:
      return 'OP Super Administrator';
    default: {
      return 'Educator';
    }
  }
}

function UserColumn(props: Props) {
  const {
    user,
    isDistrictAdmin,
    handleCopyToClipboard,
    handleUserEdit,
  } = props;

  const {id, firstName, lastName, email, schools, lastLoginAt, status, role, totalLogins} = user;

  const userName = firstName + " " + lastName
  const pending = status === UserStatus.Pending;
  
  return (
    <div className={isDistrictAdmin ? styles.widerColumnView : styles.columnView}>
      <div className={styles.column}>{text_truncate(userName, 20)}</div>
      <div className={styles.distSchoolColumn}>{email}</div>
      {isDistrictAdmin ? <div className={styles.distSchoolColumn}>{text_truncate(schools?.[0]?.name || '', 24)}</div> : null}
      <div className={styles.columnCenterAlign}>
        { pending
        ? <div className={styles.pendingCapsule}>{isDistrictAdmin ? 'Pending' : 'Invite Pending'}</div>
        : lastLoginAt ? timeAgo(Number(lastLoginAt)) : ''
        }
      </div>
      <div className={styles.columnCenterAlign}>
        {totalLogins}
      </div>
      <div className={styles.columnCenterAlign}>
        <AntTooltip
          placement="top"
          title={
            <div className={styles.userRoleTip}>
              {getRoleTip(role)}
            </div>
          }
          arrowPointAtCenter={false}
          color="#FFFFFF"
        >
          <div className={styles.userRoleIcon}>
            <LordIcon src={getIconByRole(role)} isHover={role !== UserRoleEnum.User} colors={{primary: '#121331', secondary: '#FFB073'}} size={35}/>
          </div>
        </AntTooltip>
      </div>
      <div className={styles.columnCenterAlign}>
      {pending
        ? <AntTooltip
          placement="top"
          title={
            <div className={styles.userRoleTip}>{`Click to Copy ${firstName} ${lastName}'s Invite Link`}</div>
          }
          arrowPointAtCenter={false}
          color="#FFFFFF"
          >
            <CopyToClipboard
              text={`${window.location.protocol + "//" + window.location.host}/registration?id=${id}`}
              onCopy={() => handleCopyToClipboard(`${firstName} ${lastName}'s Invite Link Copied!`)}>
              <div className={styles.iconWrapperCopy}>
                <CopyIcon width="20px" height="20px" className={styles.iconWrapper}/>
              </div>
            </CopyToClipboard>
          </AntTooltip>
        : 
        <AntTooltip
          placement="top"
          title={
            <div className={styles.userRoleTip}>{`Click to Reset ${firstName} ${lastName}'s Password`}</div>
          }
          arrowPointAtCenter={false}
          color="#FFFFFF"
          >
            <div onClick={() => handleUserEdit(user as User, 'PASSWORD_RESET')}>
              <PasswordIcon width="20px" height="20px" className={styles.iconWrapper}/>
            </div>
          </AntTooltip>
      }
      </div>
      <div className={styles.columnCenterAlign}>
        <div className={styles.actionWrapper} onClick={() => handleUserEdit(user as User, 'EDIT')}>
          <IconPencil />
        </div>
      </div>
    </div>
  );
}

export default UserColumn;
